<script setup lang="ts">
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/select'
import { useVModel } from '@vueuse/core'

import { offerRewardTypeBlockKey } from '@/components/rewards/utils/offerRewardTypeBlockContext'
import { addProductIfNotExists, removeProductFromList } from '@/utils/productHelpers'
import { validateCartItemCount } from '@/components/rewards/utils/offerRewardValidation'
import { slugBelongsTo } from '@/components/rewards/utils/rewardTypeRegistry'

import ProductAssociationBlock from '@/components/product/ProductAssociationBlock.vue'
import RewardFormBlock from '@/components/rewards/RewardFormBlock.vue'
import {
  getAccessCondition,
  createEmptyAccessConditionsState,
  getCartMinimumAmountEuros,
  hasRequiredProductsAccessError,
  hydrateAccessConditionsFromReward,
  setCartMinimumAmountEuros,
  syncRequiredProductsToReward
} from '@/components/rewards/utils/rewardAccessConditions'

import type { RadioCardOption } from '@/components/ui/AppRadioCard.vue'
import type AppFormInput from '@/components/ui/AppFormInput.vue'
import type { AccessCondition } from '@/types/offer'
import type { LoyaltyReward, PartnerProduct, RewardConditionOperator } from '@/types/types'
import { useRewardForm, type RewardFormErrorField } from './compmosable/useRewardForm'

const props = defineProps<{
  editingReward: LoyaltyReward
  formTouched?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:editingReward', value: LoyaltyReward): void
  (e: 'update:formTouched', value: boolean): void
}>()

const editingReward = useVModel(props, 'editingReward', emit)
const formTouchedModel = useVModel(props, 'formTouched', emit, { passive: true })

const offerCtx = inject(offerRewardTypeBlockKey, null)
const localFormTouched = ref(false)
const isFormTouched = offerCtx?.formTouched ?? formTouchedModel ?? localFormTouched

const { t } = useI18n()
const router = useRouter()
const { isProductFeatureDisabled, productCatalogPartners, isLoadingPartners, disabledReason, hasMinAmountError } =
  useRewardForm()

const requiredProducts = ref<PartnerProduct[]>([])
const requiredProductsOperator = ref<RewardConditionOperator>('contains_all')
const requiredProductQuantities = ref<Record<string, number>>({})

const isXForYReward = computed(() => slugBelongsTo('xForY', editingReward.value?.rewardType?.slug))
const isFreeDeliveryReward = computed(() => slugBelongsTo('freeDelivery', editingReward.value?.rewardType?.slug))

const cartItemCountTouched = ref(false)

const syncRequiredProductsToRewardModel = () => {
  const reward = editingReward.value
  if (!reward) return
  syncRequiredProductsToReward(
    reward,
    requiredProducts.value,
    requiredProductQuantities.value,
    requiredProductsOperator.value
  )
}

const accessCondition = computed<AccessCondition>({
  get: () => getAccessCondition(editingReward.value),
  set: (v) => {
    const r = editingReward.value
    if (!r) return
    const previous = r.accessCondition
    r.accessCondition = v

    if (v === 'none') {
      setCartMinimumAmountEuros(r, null)
      requiredProducts.value = []
      cartItemCount.value = null
      syncRequiredProductsToRewardModel()
    } else if (v === 'min_amount') {
      const currentMin = getCartMinimumAmountEuros(r)
      if (currentMin == null || currentMin <= 0) {
        setCartMinimumAmountEuros(r, 10)
      }
      requiredProducts.value = []
      cartItemCount.value = null
      syncRequiredProductsToRewardModel()
    } else if (v === 'products_required' && previous !== 'products_required') {
      setCartMinimumAmountEuros(r, null)
      cartItemCount.value = null
      syncRequiredProductsToRewardModel()
    } else if (v === 'cart_item_count') {
      setCartMinimumAmountEuros(r, null)
      requiredProducts.value = []
      if (cartItemCount.value == null || cartItemCount.value <= 0) {
        cartItemCount.value = 1
      }
      syncRequiredProductsToRewardModel()
    }

    isFormTouched.value = true
  }
})

const cartItemCount = computed<number | null>({
  get: () => editingReward.value?.cartItemCount ?? null,
  set: (val) => {
    if (!editingReward.value) return
    editingReward.value.cartItemCount = val
  }
})

const cartMinimumAmount = computed<number | null>({
  get: () => getCartMinimumAmountEuros(editingReward.value),
  set: (val) => {
    if (!editingReward.value) return
    setCartMinimumAmountEuros(editingReward.value, val)
  }
})

const hasCartItemCountError = computed(() => {
  if (accessCondition.value !== 'cart_item_count') return false
  return !validateCartItemCount(cartItemCount.value)
})

const hasRequiredProductsError = computed(() =>
  hasRequiredProductsAccessError(editingReward.value, requiredProducts.value)
)

const hasAccessErrors = computed(() => hasCartItemCountError.value || hasRequiredProductsError.value)

const cartItemCountErrors = computed(() =>
  cartItemCountTouched.value && hasCartItemCountError.value ? [t('pages.offer.reward.cartItemCountError')] : []
)

const showAccessConditions = computed(() => {
  if (!editingReward.value) return false
  return !isXForYReward.value && !isFreeDeliveryReward.value
})

const requiredProductsOperatorOptions = computed(() => [
  { value: 'contains_all', label: t('pages.offer.reward.requiredProductsAll') },
  { value: 'contains_any', label: t('pages.offer.reward.requiredProductsAny') }
])

const isAccessConditionOptionDisabled = (option: AccessCondition) => {
  if (option === 'none') return false
  return isProductFeatureDisabled.value
}

const accessConditionOptions = computed<(RadioCardOption & { id: AccessCondition })[]>(() => [
  {
    id: 'none',
    title: t('pages.offer.reward.accessNone'),
    description: t('pages.offer.reward.accessNoneDesc')
  },
  {
    id: 'min_amount',
    title: t('pages.offer.reward.accessMinAmount'),
    description: t('pages.offer.reward.accessMinAmountDesc'),
    disabled: isAccessConditionOptionDisabled('min_amount')
  },
  {
    id: 'products_required',
    title: t('pages.offer.reward.accessProductsRequired'),
    description: t('pages.offer.reward.accessProductsRequiredDesc'),
    disabled: isAccessConditionOptionDisabled('products_required')
  },
  {
    id: 'cart_item_count',
    title: t('pages.offer.reward.cartItemCountLabel'),
    description: t('pages.offer.reward.cartItemCountDesc'),
    disabled: isAccessConditionOptionDisabled('cart_item_count')
  }
])

const selectedAccessConditionOption = computed({
  get: () => accessConditionOptions.value.find((o) => o.id === accessCondition.value),
  set: (opt: RadioCardOption & { id: AccessCondition }) => {
    if (opt?.id) accessCondition.value = opt.id
  }
})

const touchCartItemCount = () => {
  cartItemCountTouched.value = true
  isFormTouched.value = true
}

const onRequiredProductsOperatorChange = (value: RewardConditionOperator) => {
  requiredProductsOperator.value = value
  syncRequiredProductsToRewardModel()
  isFormTouched.value = true
}

const onRequiredProductQuantitiesChange = (quantities: Record<string, number>) => {
  requiredProductQuantities.value = quantities
  syncRequiredProductsToRewardModel()
}

const handleRemoveRequiredProduct = (product: PartnerProduct) => {
  removeProductFromList(requiredProducts.value, product)
  syncRequiredProductsToRewardModel()
  isFormTouched.value = true
}

const handleSelectRequiredProduct = (product: PartnerProduct) => {
  addProductIfNotExists(requiredProducts.value, product)
  syncRequiredProductsToRewardModel()
  isFormTouched.value = true
}

const navigateToIntegrations = () => router.push({ name: 'IntegrationList' })

const applyHydratedState = (state: ReturnType<typeof hydrateAccessConditionsFromReward>) => {
  requiredProducts.value = state.requiredProducts
  requiredProductsOperator.value = state.requiredProductsOperator
  requiredProductQuantities.value = state.requiredProductQuantities
}

const hydrateFromReward = () => {
  const reward = editingReward.value
  if (!reward) return
  applyHydratedState(hydrateAccessConditionsFromReward(reward))
}

const resetOnRewardTypeChange = () => {
  applyHydratedState(createEmptyAccessConditionsState())
  isFormTouched.value = true
}

hydrateFromReward()

onMounted(() => {
  if (offerCtx?.accessConditions) {
    offerCtx.accessConditions.resetOnRewardTypeChange = resetOnRewardTypeChange
  }
})

onUnmounted(() => {
  if (offerCtx?.accessConditions) {
    offerCtx.accessConditions.resetOnRewardTypeChange = undefined
  }
})

const minAmountInputRef = ref<InstanceType<typeof AppFormInput> | null>(null)
const cartItemCountInputRef = ref<InstanceType<typeof AppFormInput> | null>(null)
const requiredProductsRef = ref<HTMLElement | null>(null)

const scrollToSection = (sectionRef: HTMLElement | null) => {
  sectionRef?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

const focusError = (field: RewardFormErrorField): boolean => {
  switch (field) {
    case 'minAmount':
      if (!hasMinAmountError.value) return false
      minAmountInputRef.value?.focus()
      return true
    case 'requiredProducts':
      if (!hasRequiredProductsError.value) return false
      scrollToSection(requiredProductsRef.value)
      return true
    case 'cartItemCount':
      if (!hasCartItemCountError.value) return false
      cartItemCountTouched.value = true
      cartItemCountInputRef.value?.focus()
      return true
    default:
      return false
  }
}

defineExpose({
  hasAccessErrors,
  hydrateFromReward,
  requiredProducts,
  requiredProductsOperator,
  requiredProductQuantities,
  focusError
})
</script>

<template>
  <RewardFormBlock v-if="showAccessConditions" :title="t('pages.offer.reward.accessConditionsTitle')">
    <div class="space-y-4">
      <AppRadioCard v-model="selectedAccessConditionOption" :options="accessConditionOptions">
        <template #1="{ checked }">
          <div class="flex min-w-0 flex-col gap-1">
            <span class="truncate text-sm font-medium text-foreground">
              {{ t('pages.offer.reward.accessMinAmount') }}
            </span>
            <span class="text-xs text-muted-foreground">
              {{ t('pages.offer.reward.accessMinAmountDesc') }}
            </span>
            <div v-if="checked" class="mt-5 w-32" @click.stop>
              <AppFormInput
                id="offer-minimumOrderAmount"
                ref="minAmountInputRef"
                v-model="cartMinimumAmount"
                type="number"
                step="0.01"
                :min="0"
                :disabled="isAccessConditionOptionDisabled('min_amount')"
                @update:model-value="isFormTouched = true"
              >
                <template #icon-right>
                  <div class="flex h-5 w-5 items-center justify-center text-sm text-muted-foreground">€</div>
                </template>
              </AppFormInput>
            </div>
          </div>
        </template>

        <template #2="{ checked }">
          <div class="flex min-w-0 flex-col gap-1">
            <span class="truncate text-sm font-medium text-foreground">
              {{ t('pages.offer.reward.accessProductsRequired') }}
            </span>
            <span class="text-xs text-muted-foreground">
              {{ t('pages.offer.reward.accessProductsRequiredDesc') }}
            </span>
            <div v-if="checked" ref="requiredProductsRef" class="mt-5" @click.stop>
              <ProductAssociationBlock
                v-model:associated-products="requiredProducts"
                :is-loading="isLoadingPartners"
                :partners="productCatalogPartners"
                :product-quantities="requiredProductQuantities"
                :show-required-quantity="true"
                :error-message="hasRequiredProductsError ? t('pages.offer.reward.accessProductsRequiredError') : null"
                @update:associated-products="syncRequiredProductsToRewardModel"
                @update:product-quantities="onRequiredProductQuantitiesChange"
                @select-product="handleSelectRequiredProduct"
                @remove-product="handleRemoveRequiredProduct"
                @configure="navigateToIntegrations"
              >
                <template #header>
                  <div class="flex items-center gap-4">
                    <span class="shrink-0 text-sm text-muted-foreground">
                      {{ t('pages.offer.reward.clientMustHave') }}
                    </span>
                    <Select
                      :model-value="requiredProductsOperator"
                      :disabled="isAccessConditionOptionDisabled('products_required')"
                      @update:model-value="onRequiredProductsOperatorChange($event as RewardConditionOperator)"
                    >
                      <SelectTrigger class="h-10 flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          v-for="option in requiredProductsOperatorOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </template>
              </ProductAssociationBlock>
            </div>
          </div>
        </template>

        <template #3="{ checked }">
          <div class="flex min-w-0 flex-col gap-1">
            <span class="truncate text-sm font-medium text-foreground">
              {{ t('pages.offer.reward.cartItemCountLabel') }}
            </span>
            <span class="text-xs text-muted-foreground">
              {{ t('pages.offer.reward.cartItemCountDesc') }}
            </span>
            <div v-if="checked" class="mt-5 w-40" @click.stop>
              <AppFormInput
                id="offer-cartItemCount"
                ref="cartItemCountInputRef"
                v-model="cartItemCount"
                type="number"
                :min="1"
                step="1"
                placeholder="3"
                :errors="cartItemCountErrors"
                error-classes="!mt-1 !text-xs"
                :disabled="isAccessConditionOptionDisabled('cart_item_count')"
                @update:model-value="touchCartItemCount"
              >
                <template #icon-right>
                  <div class="flex h-5 w-auto items-center justify-center px-2 text-xs text-muted-foreground">
                    {{ t('pages.offer.reward.cartItemCountUnit') }}
                  </div>
                </template>
              </AppFormInput>
            </div>
          </div>
        </template>
      </AppRadioCard>

      <BaseTip
        v-if="isProductFeatureDisabled"
        severity="secondary"
        icon-name="IconoirInfoCircle"
        :text="t(`pages.offer.reward.accessConditionsDisabledReason.${disabledReason}`)"
      />
    </div>
  </RewardFormBlock>
</template>
